// ────────────────────────────────────────────────────────────────────────────
// Document-wide options: page setup and the colour / type theme.
//
// The font list is limited to the three families react-pdf embeds without a
// registration step. Shipping a custom brand font means bundling the file and
// registering it at startup — worth doing, but it is a separate decision from
// the layout model, so the picker stays honest about what actually renders.
// ────────────────────────────────────────────────────────────────────────────
import {
  ColorField,
  InspectorGroup,
  InspectorRow,
  NumberField,
  SelectField,
} from './controls';
import { RotateCcw } from 'lucide-react';
import {
  BAND_COLUMNS,
  type Band,
  type BandColumn,
  type DocumentLayout,
} from '../lib/types';
import {
  resolveTypeScale,
  TYPE_SCALE_FIELDS,
  TYPE_SCALE_LABEL,
  TYPE_SCALE_TARGET,
} from '../lib/typeScale';
import {
  FONT_FAMILIES,
  PAGE_ORIENTATIONS,
  PAGE_ORIENTATION_LABEL,
  PAGE_SIZES,
  type FontFamily,
  type PageOrientation,
  type PageSettings,
  type PageSize,
  type ThemeSettings,
} from '../lib/types';

const PAGE_SIZE_OPTIONS = PAGE_SIZES.map((size) => ({
  value: size,
  label: size === 'A4' ? 'A4' : 'US Letter',
}));

const FONT_OPTIONS = FONT_FAMILIES.map((family) => ({
  value: family,
  label: family === 'Times-Roman' ? 'Times' : family,
}));

export function PageStyleInspector({
  layout,
  onChange,
}: {
  layout: DocumentLayout;
  /** Patch of the whole layout — page, theme and the header-grid settings. */
  onChange: (next: Partial<DocumentLayout>) => void;
}) {
  const { page, theme } = layout;
  const setPage = (next: Partial<PageSettings>) => onChange({ page: { ...page, ...next } });
  const setTheme = (next: Partial<ThemeSettings>) => onChange({ theme: { ...theme, ...next } });
  const setLayout = (next: Partial<DocumentLayout>) => onChange(next);
  const scale = resolveTypeScale(theme);

  // Bands above the line table, so the panel can show what the grid is doing
  // even when nothing has been declared explicitly.
  const headerBands: Band[] = (() => {
    const i = layout.bands.findIndex((b) => b.kind === 'lines');
    return i === -1 ? [] : layout.bands.slice(0, i);
  })();
  let usedColumns = 1;
  let derivedGapAfter = 0;
  for (const band of headerBands) {
    usedColumns = Math.max(usedColumns, band.column ?? 1);
    derivedGapAfter = Math.max(derivedGapAfter, band.spaceAfterPt);
  }
  const headerColumns: number = layout.headerColumns ?? usedColumns;
  const weights: number[] = layout.columnWeights ?? [];

  return (
    <div className="space-y-2">
      <InspectorGroup title="Page">
        <InspectorRow label="Size">
          <SelectField<PageSize>
            value={page.size}
            options={PAGE_SIZE_OPTIONS}
            onChange={(size) => setPage({ size })}
          />
        </InspectorRow>
        <InspectorRow label="Orientation" hint="Landscape fits more columns">
          <SelectField<PageOrientation>
            value={page.orientation ?? 'portrait'}
            options={PAGE_ORIENTATIONS.map((o) => ({
              value: o,
              label: PAGE_ORIENTATION_LABEL[o],
            }))}
            onChange={(orientation) => setPage({ orientation })}
          />
        </InspectorRow>
        <InspectorRow label="Margin" hint="36pt ≈ 13mm">
          <NumberField
            value={page.marginPt}
            min={12}
            max={90}
            suffix="pt"
            onChange={(marginPt) => setPage({ marginPt })}
          />
        </InspectorRow>
      </InspectorGroup>

      <InspectorGroup title="Header layout">
        <InspectorRow label="Columns" hint="Everything above the line items">
          <SelectField<string>
            value={String(headerColumns)}
            options={BAND_COLUMNS.map((c) => ({
              value: String(c),
              label: c === 1 ? 'Single column' : `${c} columns`,
            }))}
            onChange={(value) => setLayout({ headerColumns: Number(value) as BandColumn })}
          />
        </InspectorRow>

        {headerColumns > 1 && (
          <>
            <div className="mt-1 flex items-center justify-between gap-2 py-1">
              <p className="text-xs text-slate-600">Column widths</p>
              <div className="flex items-center gap-1">
                {Array.from({ length: headerColumns }, (_, i) => (
                  <NumberField
                    key={i}
                    value={weights[i] ?? 1}
                    min={1}
                    max={20}
                    onChange={(value) => {
                      const next = Array.from(
                        { length: headerColumns },
                        (_, j) => weights[j] ?? 1,
                      );
                      next[i] = value;
                      setLayout({ columnWeights: next });
                    }}
                  />
                ))}
              </div>
            </div>
            <p className="mb-1 text-[10px] leading-snug text-slate-400">
              Relative, not points — 3 and 2 gives a 60/40 split.
            </p>
          </>
        )}

        <InspectorRow label="Gap above">
          <NumberField
            value={layout.headerGapBeforePt ?? 0}
            min={0}
            max={120}
            suffix="pt"
            onChange={(headerGapBeforePt) => setLayout({ headerGapBeforePt })}
          />
        </InspectorRow>
        <InspectorRow label="Gap below" hint={layout.headerGapAfterPt === undefined ? 'From the bands' : undefined}>
          {layout.headerGapAfterPt !== undefined && (
            <button
              type="button"
              title="Go back to spacing from the bands"
              aria-label="Reset the gap below the header"
              onClick={() => setLayout({ headerGapAfterPt: undefined })}
              className="text-slate-400 hover:text-sky-700"
            >
              <RotateCcw className="h-3 w-3" />
            </button>
          )}
          <NumberField
            value={layout.headerGapAfterPt ?? derivedGapAfter}
            min={0}
            max={120}
            suffix="pt"
            onChange={(headerGapAfterPt) => setLayout({ headerGapAfterPt })}
          />
        </InspectorRow>
        <p className="mt-1 text-[10px] leading-snug text-slate-400">
          Set each band's column in its own panel. Bands from the line items down always run the
          full width.
        </p>
      </InspectorGroup>

      <InspectorGroup title="Type">
        <InspectorRow label="Font">
          <SelectField<FontFamily>
            value={theme.fontFamily}
            options={FONT_OPTIONS}
            onChange={(fontFamily) => setTheme({ fontFamily })}
          />
        </InspectorRow>
        <InspectorRow label="Body size" hint="Everything else scales off this">
          <NumberField
            value={theme.baseFontSizePt}
            min={6}
            max={14}
            suffix="pt"
            onChange={(baseFontSizePt) => setTheme({ baseFontSizePt })}
          />
        </InspectorRow>

        {TYPE_SCALE_FIELDS.map((field) => {
          const pinned = theme[field] !== undefined;
          return (
            <InspectorRow
              key={field}
              label={TYPE_SCALE_LABEL[field]}
              hint={pinned ? 'Fixed' : 'Follows body size'}
            >
              {pinned && (
                <button
                  type="button"
                  title="Follow the body size again"
                  aria-label={`Reset ${TYPE_SCALE_LABEL[field]} to follow the body size`}
                  onClick={() => setTheme({ [field]: undefined })}
                  className="text-slate-400 hover:text-sky-700"
                >
                  <RotateCcw className="h-3 w-3" />
                </button>
              )}
              <NumberField
                // Shows the size that is actually printing, pinned or derived —
                // editing it is what pins it.
                value={scale[TYPE_SCALE_TARGET[field]]}
                min={5}
                max={40}
                suffix="pt"
                onChange={(value) => setTheme({ [field]: value })}
              />
            </InspectorRow>
          );
        })}
        <p className="mt-1 text-[10px] leading-snug text-slate-400">
          These follow the body size until you change one. The document title has its own size in
          the Header band.
        </p>
      </InspectorGroup>

      <InspectorGroup title="Colours">
        <InspectorRow label="Body text">
          <ColorField value={theme.textColor} onChange={(textColor) => setTheme({ textColor })} />
        </InspectorRow>
        <InspectorRow label="Secondary text">
          <ColorField value={theme.mutedColor} onChange={(mutedColor) => setTheme({ mutedColor })} />
        </InspectorRow>
        <InspectorRow label="Accent" hint="Title and table headings">
          <ColorField
            value={theme.accentColor}
            onChange={(accentColor) => setTheme({ accentColor })}
          />
        </InspectorRow>
        <InspectorRow label="Table header fill">
          <ColorField
            value={theme.tableHeadColor}
            onChange={(tableHeadColor) => setTheme({ tableHeadColor })}
          />
        </InspectorRow>
        <InspectorRow label="Rules and borders">
          <ColorField
            value={theme.borderColor}
            onChange={(borderColor) => setTheme({ borderColor })}
          />
        </InspectorRow>
      </InspectorGroup>

      <p className="text-[10px] leading-snug text-slate-400">
        The logo comes from Settings → Organisation. Turn it on in the Header band.
      </p>
    </div>
  );
}
